import React from "react";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from "react-router-dom";

// Providers moved into the root route element so all children have router context
import { NavigationProvider } from "@/providers/vezgo/context/navigation";
import { Wealthica2NavigationProvider } from "@/providers/wealthica2/context/navigation/NavigationProvider";
import { AddToPortfolioProvider } from "@/components/portfolio/add-to-portfolio/AddToPortfolioProvider";
import { AddPositionsProvider } from "@/components/investments/add-positions/AddPositionsProvider";

// Layouts
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { AppLayout } from "@/components/layouts/AppLayout";

// Pages
import Auth from "@/pages/Auth";
import Investments from "@/pages/Investments";
import VezgoConnect from "@/pages/VezgoConnect";
import VezgoCallback from "@/pages/VezgoCallback";
import ExploreOffers from "@/pages/ExploreOffers";
import BuildPortfolio from "@/pages/BuildPortfolio";
import ModernWealthPlaybook from "@/pages/content/ModernWealthPlaybook";
import HoldingCashVsInvesting from "@/pages/tools/HoldingCashVsInvesting";
import InvestingQuiz from "@/pages/tools/InvestorRiskProfile";
import EligibilityTest from "@/pages/tools/EligibilityTest";
import CanadianInvestmentAccountsGlossary from "@/pages/content/CanadianInvestmentAccountsGlossary";
import MarketplaceGuide from "@/pages/content/MarketplaceGuide";
import AngelNetworksAccelerators from "@/pages/content/AngelNetworksAccelerators";

import GettingStartedAlternatives from "@/pages/content/GettingStartedAlternatives";
import GettingStartedAlternatives2 from "@/pages/content/GettingStartedAlternatives2";

import ProvincialRegulatorsGlossary from "@/pages/content/ProvincialRegulatorsGlossary";
import FundProfile from "@/pages/FundProfile";
import CsvUtilsPage from "@/pages/CsvUtilsPage";
import Dashboard from "@/pages/Dashboard";
import { SettingsLayout } from "@/pages/settings/SettingsLayout";
import { AdminPage } from "@/pages/AdminPage";
import { EligibilityCheck } from "@/pages/settings/EligibilityCheck";
import ConnectInvestments from "@/pages/ConnectInvestments";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import AdminBackfillFundImages from "@/pages/AdminBackfillFundImages";
import Wizard from "@/pages/Wizard";
import PortfolioTools from "@/pages/PortfolioTools";

// Root element that wraps providers and renders child routes
function RootProviders() {
  return (
    <>
      <NavigationProvider>
        <Wealthica2NavigationProvider>
          <AddToPortfolioProvider>
            <AddPositionsProvider>
              <Outlet />
            </AddPositionsProvider>
          </AddToPortfolioProvider>
        </Wealthica2NavigationProvider>
      </NavigationProvider>
      <ScrollRestoration getKey={(location) => location.key || location.pathname} />
    </>
  );
}

// Lightweight client redirect to an external URL
function ExternalRedirect({ href }: { href: string }) {
  React.useEffect(() => {
    window.location.replace(href);
  }, [href]);
  return null;
}

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <RootProviders />,
      children: [
        // Public routes with AuthLayout
        {
          element: (
            <AuthLayout>
              <Outlet />
            </AuthLayout>
          ),
          children: [
            { path: "/signup", element: <Navigate to="/auth?mode=signup" replace /> },
            { path: "/signin", element: <Navigate to="/auth?mode=signin" replace /> },
            { path: "/auth", element: <Auth /> },
          ],
        },

        // Protected routes with AppLayout
        {
          element: (
            <AppLayout>
              <Outlet />
            </AppLayout>
          ),
          children: [
            { path: "/", element: <Dashboard /> },
            { path: "/explore", element: <ExploreOffers /> },
            { path: "/portfolios", element: <Dashboard /> },
            { path: "/portfolio/tools", element: <PortfolioTools /> },
            { path: "/portfolios/build/:portfolioId", element: <BuildPortfolio /> },
            { path: "/funds/:fundId", element: <FundProfile /> },
            { path: "/investments", element: <Investments /> },
            { path: "/assets", element: <Assets /> },
            { path: "/assets/:assetId", element: <AssetDetail /> },
            { path: "/investments/connect", element: <ConnectInvestments /> },
            { path: "/investments/connect/vezgo", element: <VezgoCallback /> },
            { path: "/vezgo-connect", element: <VezgoConnect /> },
            { path: "/admin/backfill-fund-images", element: <AdminBackfillFundImages /> },
            { path: "/settings", element: <SettingsLayout /> },
            { path: "/settings/account", element: <SettingsLayout /> },
            { path: "/settings/billing", element: <SettingsLayout /> },
            { path: "/settings/eligibility", element: <EligibilityCheck /> },
            { path: "/admin", element: <AdminPage /> },
          ],
        },

        // Utility routes outside main app layout
        { path: "/csv-utils", element: <CsvUtilsPage /> },
        { path: "/wizard-test", element: <Wizard /> },

        // Public content routes (no auth required)
        { path: "/content/can/modern-wealth-playbook", element: <ModernWealthPlaybook /> },
        { path: "/content/can/marketplace-guide", element: <MarketplaceGuide /> },
        { path: "/content/can/angel-networks-accelerators", element: <AngelNetworksAccelerators /> },
        { path: "/tools/holding-cash-vs-investing", element: <HoldingCashVsInvesting /> },

        // WIP Articles 
        { path: "/content/can/getting-started-alternative", element: <GettingStartedAlternatives /> },
        { path: "/content/can/getting-started-alternative-v2", element: <GettingStartedAlternatives2 /> },

        // Embedded Tally.so Surveys 
        { path: "/tools/investor-risk-profile", element: <InvestingQuiz /> },
        { path: "/tools/eligibility-test", element: <EligibilityTest /> },

        // Policy routes: force full-page load of static files from /public (dev-safe)
        { path: "/privacy-policy", element: <ExternalRedirect href="/privacy-policy/index.html" /> },
        { path: "/terms-of-service", element: <ExternalRedirect href="/terms-of-service/index.html" /> },
        { path: "/cookies", element: <ExternalRedirect href="/cookies/index.html" /> },

        { path: "/glossary/registered-accounts", element: <CanadianInvestmentAccountsGlossary /> },
        { path: "/glossary/provincial-regulators", element: <ProvincialRegulatorsGlossary /> },
        
        // Fallback
        { path: "*", element: <Navigate to="/" replace /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);
